import { apiClient } from '@/api/client';
import type { ApiResponseWrapper } from '@/types/api.types';

export type PolicyCategory =
  | 'handbook'
  | 'code_of_conduct'
  | 'it_security'
  | 'health_safety'
  | 'leave'
  | 'other';
export type PolicyStatus = 'active' | 'withdrawn' | 'archived';
export type PolicyVersionStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'scheduled'
  | 'published'
  | 'superseded'
  | 'withdrawn';
export type PolicyBlockKind = 'heading' | 'paragraph' | 'bullets' | 'spacer';
export type AcknowledgementMode = 'none' | 'click' | 'drawn';
export type PolicyAssignmentStatus =
  | 'pending'
  | 'acknowledged'
  | 'carried_forward'
  | 'not_required'
  | 'exempted'
  | 'removed'
  | 'superseded'
  | 'withdrawn';

export interface PolicyAudience {
  companyIds: string[];
  departmentIds: string[];
  teamIds: string[];
  gradeIds: string[];
  jobTitleIds: string[];
  employmentTypeIds: string[];
  includeUserIds: string[];
  excludeUserIds: string[];
}

export interface PolicyBlock {
  kind: PolicyBlockKind;
  text?: string;
  items?: string[];
}

export interface PolicyVersionSummary {
  _id: string;
  policyId: string;
  version: number;
  status: PolicyVersionStatus;
  title: string;
  effectiveAt: string;
  acknowledgement: { mode: AcknowledgementMode; windowDays: number };
  publishedAt?: string;
}

export interface PolicyVersion extends PolicyVersionSummary {
  contentKind: 'blocks' | 'pdf';
  blocks: PolicyBlock[];
  file?: { sha256: string; size: number; originalName?: string };
  artifact?: { sha256: string; size: number };
  summaryOfChanges?: string;
  requiresReacknowledgement: boolean;
  contentSha256?: string;
  createdBy: string;
  submittedBy?: string;
  approvedBy?: string;
  approvedAt?: string;
  updatedAt?: string;
}

export interface Policy {
  _id: string;
  ownerCompanyId?: string;
  name: string;
  category: PolicyCategory;
  audience: PolicyAudience;
  currentVersionId?: string;
  status: PolicyStatus;
  withdrawnAt?: string;
  withdrawReason?: string;
}

export interface PolicyListItem extends Policy {
  editable: boolean;
  current: PolicyVersionSummary | null;
  latest: PolicyVersionSummary | null;
}

export interface PolicyDetail {
  policy: Policy;
  versions: PolicyVersion[];
  editable: boolean;
}

export interface DraftInput {
  title?: string;
  contentKind?: 'blocks' | 'pdf';
  blocks?: PolicyBlock[];
  summaryOfChanges?: string;
  acknowledgement?: { mode: AcknowledgementMode; windowDays: number };
  requiresReacknowledgement?: boolean;
  effectiveAt?: string;
}

export interface PolicyReport {
  versionId: string;
  version: number;
  mode: AcknowledgementMode;
  asOf: string;
  summary: {
    audience: number;
    acknowledged: number;
    carriedForward: number;
    pending: number;
    overdue: number;
    exempted: number;
    notRequired: number;
    removed: number;
    acknowledgedThenLeft: number;
  };
  breakdown: Array<{
    companyId: string;
    departmentId: string | null;
    departmentName: string | null;
    audience: number;
    complete: number;
    pending: number;
  }>;
}

export interface OutstandingRow {
  assignmentId: string;
  userId: string;
  name: string;
  email?: string;
  employeeId?: string;
  companyIds: string[];
  assignedAt: string;
  dueAt?: string;
  overdue: boolean;
  viewed: boolean;
  remindersSent: number;
}

export interface OutstandingPage {
  data: OutstandingRow[];
  total: number;
  page: number;
  limit: number;
}

export interface EmployeePolicyRow {
  _id: string;
  policyId: string;
  policyVersionId: string;
  policyName: string;
  versionNumber: number;
  status: PolicyAssignmentStatus;
  inAudience: boolean;
  assignedAt: string;
  dueAt?: string;
  acknowledgedAt?: string;
  overdue: boolean;
}

export interface MyPolicy {
  assignmentId: string;
  policyId: string;
  policyVersionId: string;
  policyName: string;
  title: string;
  version: number;
  versionStatus: PolicyVersionStatus;
  summaryOfChanges?: string;
  effectiveAt: string;
  mode: AcknowledgementMode;
  artifactSha256?: string;
  status: PolicyAssignmentStatus;
  assignedAt: string;
  dueAt?: string;
  overdue: boolean;
  viewedAt?: string;
  acknowledgedAt?: string;
  awaitingMe: boolean;
}

export interface AcknowledgementConsent {
  click: { version: string; text: string };
  drawn: { version: string; text: string };
}

/** Policy owner for API routing and its effective timezone for display. */
export type PolicyContext = { companyId?: string; timeZone?: string };

const base = ({ companyId }: PolicyContext): string =>
  companyId ? `/companies/${companyId}/hr/policies` : '/hr/policies';
const unwrap = <T>(data: ApiResponseWrapper<T>): T => data.data;

export const POLICY_CATEGORY_LABELS: Record<PolicyCategory, string> = {
  handbook: 'Handbook',
  code_of_conduct: 'Code of conduct',
  it_security: 'IT and security',
  health_safety: 'Health and safety',
  leave: 'Leave',
  other: 'Other',
};

export const EMPTY_AUDIENCE: PolicyAudience = {
  companyIds: [],
  departmentIds: [],
  teamIds: [],
  gradeIds: [],
  jobTitleIds: [],
  employmentTypeIds: [],
  includeUserIds: [],
  excludeUserIds: [],
};

export async function listPolicies(
  context: PolicyContext,
  includeArchived = false
): Promise<PolicyListItem[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<PolicyListItem[]>>(base(context), {
    params: includeArchived ? { includeArchived: 'true' } : undefined,
  });
  return unwrap(data);
}

export async function createPolicy(
  context: PolicyContext,
  input: { name: string; category: PolicyCategory; audience?: Partial<PolicyAudience> }
): Promise<{ policy: Policy; version: PolicyVersion }> {
  const { data } = await apiClient.post<
    ApiResponseWrapper<{ policy: Policy; version: PolicyVersion }>
  >(base(context), input);
  return unwrap(data);
}

export async function getPolicy(context: PolicyContext, policyId: string): Promise<PolicyDetail> {
  const { data } = await apiClient.get<ApiResponseWrapper<PolicyDetail>>(
    `${base(context)}/${policyId}`
  );
  return unwrap(data);
}

export async function updatePolicy(
  context: PolicyContext,
  policyId: string,
  input: { name?: string; category?: PolicyCategory }
): Promise<Policy> {
  const { data } = await apiClient.patch<ApiResponseWrapper<Policy>>(
    `${base(context)}/${policyId}`,
    input
  );
  return unwrap(data);
}

export async function setPolicyAudience(
  context: PolicyContext,
  policyId: string,
  audience: Partial<PolicyAudience>,
  reason?: string
): Promise<{ policy: Policy; reconciled: Record<string, number> | null }> {
  const { data } = await apiClient.put<
    ApiResponseWrapper<{ policy: Policy; reconciled: Record<string, number> | null }>
  >(`${base(context)}/${policyId}/audience`, { ...audience, ...(reason && { reason }) });
  return unwrap(data);
}

export async function previewAudience(
  context: PolicyContext,
  audience: Partial<PolicyAudience>
): Promise<{ total: number; byCompany: Array<{ companyId: string; count: number }> }> {
  const { data } = await apiClient.post<
    ApiResponseWrapper<{ total: number; byCompany: Array<{ companyId: string; count: number }> }>
  >(`${base(context)}/audience-preview`, audience);
  return unwrap(data);
}

export async function createPolicyDraft(
  context: PolicyContext,
  policyId: string
): Promise<PolicyVersion> {
  const { data } = await apiClient.post<ApiResponseWrapper<PolicyVersion>>(
    `${base(context)}/${policyId}/versions`
  );
  return unwrap(data);
}

export async function getPolicyVersion(
  context: PolicyContext,
  versionId: string
): Promise<PolicyVersion> {
  const { data } = await apiClient.get<ApiResponseWrapper<PolicyVersion>>(
    `${base(context)}/versions/${versionId}`
  );
  return unwrap(data);
}

export async function savePolicyDraft(
  context: PolicyContext,
  versionId: string,
  input: DraftInput
): Promise<PolicyVersion> {
  const { data } = await apiClient.put<ApiResponseWrapper<PolicyVersion>>(
    `${base(context)}/versions/${versionId}`,
    input
  );
  return unwrap(data);
}

export async function uploadPolicyPdf(
  context: PolicyContext,
  versionId: string,
  file: File
): Promise<PolicyVersion> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<ApiResponseWrapper<PolicyVersion>>(
    `${base(context)}/versions/${versionId}/content-file`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return unwrap(data);
}

export type PolicyVersionCommand = 'submit' | 'approve' | 'publish' | 'discard';

export async function runPolicyVersionCommand(
  context: PolicyContext,
  versionId: string,
  command: PolicyVersionCommand
): Promise<void> {
  await apiClient.post(`${base(context)}/versions/${versionId}/${command}`);
}

export async function returnPolicyVersion(
  context: PolicyContext,
  versionId: string,
  reason: string
): Promise<void> {
  await apiClient.post(`${base(context)}/versions/${versionId}/return`, { reason });
}

export async function withdrawPolicy(
  context: PolicyContext,
  policyId: string,
  reason: string
): Promise<void> {
  await apiClient.post(`${base(context)}/${policyId}/withdraw`, { reason });
}

export async function archivePolicy(context: PolicyContext, policyId: string): Promise<void> {
  await apiClient.post(`${base(context)}/${policyId}/archive`);
}

export async function previewPolicyVersion(
  context: PolicyContext,
  versionId: string
): Promise<string> {
  const { data } = await apiClient.get<ApiResponseWrapper<{ html: string }>>(
    `${base(context)}/versions/${versionId}/preview`
  );
  return unwrap(data).html;
}

export async function downloadPolicyPdf(context: PolicyContext, versionId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`${base(context)}/versions/${versionId}/artifact`, {
    responseType: 'blob',
  });
  return data;
}

export async function getPolicyReport(
  context: PolicyContext,
  versionId: string
): Promise<PolicyReport> {
  const { data } = await apiClient.get<ApiResponseWrapper<PolicyReport>>(
    `${base(context)}/versions/${versionId}/report`
  );
  return unwrap(data);
}

export async function listOutstanding(
  context: PolicyContext,
  versionId: string,
  params: { page?: number; limit?: number; overdueOnly?: boolean } = {}
): Promise<OutstandingPage> {
  const { data } = await apiClient.get<ApiResponseWrapper<OutstandingPage>>(
    `${base(context)}/versions/${versionId}/outstanding`,
    { params }
  );
  return unwrap(data);
}

export async function exportAcknowledgements(
  context: PolicyContext,
  versionId: string
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`${base(context)}/versions/${versionId}/export`, {
    responseType: 'blob',
  });
  return data;
}

export async function exemptAssignment(
  context: PolicyContext,
  assignmentId: string,
  reason: string
): Promise<void> {
  await apiClient.post(`${base(context)}/assignments/${assignmentId}/exempt`, { reason });
}

export async function listEmployeePolicies(
  context: PolicyContext,
  userId: string
): Promise<EmployeePolicyRow[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<EmployeePolicyRow[]>>(
    `${base(context)}/employees/${userId}`
  );
  return unwrap(data);
}

export async function listMyPolicies(): Promise<MyPolicy[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<MyPolicy[]>>('/me/hr-policies');
  return unwrap(data);
}

export async function getAcknowledgementConsent(): Promise<AcknowledgementConsent> {
  const { data } = await apiClient.get<ApiResponseWrapper<AcknowledgementConsent>>(
    '/me/hr-policies/consent'
  );
  return unwrap(data);
}

export async function downloadMyPolicyPdf(assignmentId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/me/hr-policies/${assignmentId}/artifact`, {
    responseType: 'blob',
  });
  return data;
}

export async function markPolicyViewed(assignmentId: string): Promise<void> {
  await apiClient.post(`/me/hr-policies/${assignmentId}/view`);
}

export async function acknowledgePolicy(
  assignmentId: string,
  input: { consentVersion: string; artifactSha256: string; signatureImage?: string }
): Promise<{ status: string; acknowledgedAt: string }> {
  const { data } = await apiClient.post<
    ApiResponseWrapper<{ status: string; acknowledgedAt: string }>
  >(`/me/hr-policies/${assignmentId}/acknowledge`, input);
  return unwrap(data);
}
